import React from 'react'
import { useEffect, useState } from 'react'
import type { Session } from '@supabase/supabase-js'
import { alertsApi, savedPostingsApi, type Alert, type Posting } from '../services/api'
import { reportError } from '../utils/errors'
import { Search, MapPin, Building2, Landmark, BellOff, X, Star } from 'lucide-react'
import PostingDetailModal from './PostingDetailModal'

interface Props {
  alert: Alert
  session: Session
  onClose: () => void
  backfill?: boolean
  highlightPostingId?: string
}

// ── Deadline helpers ───────────────────────────────────────────────────────────

export function deadlineCls(value: string | null): string {
  if (!value) return 'bg-gray-100 text-gray-500'
  const days = (new Date(value).getTime() - Date.now()) / 86_400_000
  if (days < 7)  return 'bg-red-100 text-red-700'
  if (days < 30) return 'bg-orange-100 text-orange-700'
  return 'bg-gray-100 text-gray-500'
}

export function formatDeadline(value: string | null): string {
  if (!value) return 'Scadenza —'
  return `Scade il ${new Date(value).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}`
}

// ── Filter pills ──────────────────────────────────────────────────────────────

interface Pill { icon?: React.ReactNode; text: string; cls: string }

function FilterPills({ filters }: { filters: Record<string, unknown> }) {
  const pills: Pill[] = []
  const kws = Array.isArray(filters.keywords) ? (filters.keywords as string[]) : filters.keyword ? [filters.keyword as string] : []
  kws.forEach(kw => pills.push({ icon: <Search size={10} />, text: kw, cls: 'bg-gray-100 text-gray-700' }))
  if (filters.region)   pills.push({ icon: <MapPin size={10} />,    text: filters.region as string,   cls: 'bg-blue-100 text-blue-700' })
  if (filters.sector)   pills.push({ icon: <Building2 size={10} />, text: filters.sector as string, cls: 'bg-green-100 text-green-700' })
  const entiArr: string[] = Array.isArray(filters.enti) ? (filters.enti as string[]) : []
  entiArr.forEach(e => pills.push({ icon: <Landmark size={10} />, text: e, cls: 'bg-sky-100 text-sky-700' }))

  if (pills.length === 0) pills.push({ text: 'Tutti i concorsi', cls: 'bg-gray-100 text-gray-500' })
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {pills.map((p, i) => (
        <span key={i} className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1 ${p.cls}`}>
          {p.icon}{p.text}
        </span>
      ))}
    </div>
  )
}

// ── Posting row ───────────────────────────────────────────────────────────────

function formatPublished(value: string | null): string | null {
  if (!value) return null
  return new Date(value).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })
}

interface PostingRowProps {
  posting: Posting
  zebra: boolean
  highlight: boolean
  saved: boolean
  onToggleSave: () => void
  onOpenDetail: () => void
}

function PostingRow({ posting, zebra, highlight, saved, onToggleSave, onOpenDetail }: PostingRowProps) {
  const published = formatPublished(posting.published_at)
  const baseBg = highlight ? 'bg-sky-50' : zebra ? 'bg-slate-50' : 'bg-white'
  const borderCls = highlight ? 'border-sky-200' : 'border-gray-100'

  return (
    <li className={`mb-2 border rounded-xl ${borderCls} ${highlight ? 'ring-2 ring-sky-400' : ''}`}>
      <div className={`flex items-start rounded-xl ${baseBg}`}>
        {/* Main clickable area */}
        <button
          onClick={onOpenDetail}
          className="flex-1 min-w-0 text-left py-3.5 pl-3 pr-1 focus:outline-none"
        >
          <div className="min-w-0">
            {/* Published + deadline on the same row */}
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-xs text-gray-400">{published ?? ''}</span>
              <span className={`shrink-0 text-xs font-medium rounded-full px-2.5 py-0.5 ${deadlineCls(posting.deadline)}`}>
                {formatDeadline(posting.deadline)}
              </span>
            </div>
            <p className="font-semibold text-sm leading-snug mb-1 text-gray-900">
              {posting.title}
            </p>
            {/* Ente — own row, wraps on mobile */}
            {posting.ente && (
              <p className="text-xs text-gray-500 mb-1.5 leading-snug wrap-break-word">{posting.ente}</p>
            )}
            <div className="flex items-center justify-between gap-2">
              {posting.locations && posting.locations.length > 0 ? (
                <span className="inline-flex items-center gap-1 text-xs text-gray-400 min-w-0">
                  <MapPin size={10} className="shrink-0" />
                  <span className="truncate">
                    {posting.locations.length > 2
                      ? `${posting.locations.slice(0, 2).join(' · ')} +${posting.locations.length - 2}`
                      : posting.locations.join(' · ')}
                  </span>
                </span>
              ) : <span />}
              <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 bg-gray-100 text-gray-500">
                Dettagli
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </span>
            </div>
          </div>
        </button>

        {/* Star button — quick save from list */}
        <button
          onClick={onToggleSave}
          aria-label={saved ? 'Rimuovi dai preferiti' : 'Salva nei preferiti'}
          className={`shrink-0 p-2.5 pt-3.5 transition-colors ${saved ? 'text-amber-400 hover:text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
        >
          <Star size={15} fill={saved ? 'currentColor' : 'none'} />
        </button>
      </div>
    </li>
  )
}

// ── Sorting ───────────────────────────────────────────────────────────────────

export function sortPostings(postings: Posting[]): Posting[] {
  return [...postings].sort((a, b) => {
    const statusOrder = (p: Posting) => p.status === 'OPEN' ? 0 : 1
    if (statusOrder(a) !== statusOrder(b)) return statusOrder(a) - statusOrder(b)
    return (b.published_at ?? '').localeCompare(a.published_at ?? '')
  })
}

// ── Modal ─────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20

export default function PostingsModal({ alert, session, onClose, backfill, highlightPostingId }: Props) {
  const [items, setItems] = useState<Posting[]>([])
  const [total, setTotal] = useState(0)
  const [nextPage, setNextPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState('')
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    Promise.all([
      alertsApi.getMatches(alert.id, session, 0, PAGE_SIZE),
      savedPostingsApi.listIds(session),
    ])
      .then(([res, ids]) => {
        setItems(sortPostings(res.items))
        setTotal(res.total)
        setHasMore(res.has_more)
        setNextPage(1)
        setSavedIds(new Set(ids))
      })
      .catch(e => setError(reportError(e)))
      .finally(() => setLoading(false))
  }, [alert.id])

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function loadMore() {
    setLoadingMore(true)
    try {
      const res = await alertsApi.getMatches(alert.id, session, nextPage, PAGE_SIZE)
      setItems(prev => sortPostings([...prev, ...res.items]))
      setHasMore(res.has_more)
      setNextPage(p => p + 1)
    } catch (e) {
      setError(reportError(e))
    } finally {
      setLoadingMore(false)
    }
  }

  async function toggleSave(postingId: string) {
    if (savedIds.has(postingId)) {
      setSavedIds(prev => { const s = new Set(prev); s.delete(postingId); return s })
      await savedPostingsApi.unsave(postingId, session).catch(() => {
        setSavedIds(prev => new Set([...prev, postingId]))
      })
    } else {
      setSavedIds(prev => new Set([...prev, postingId]))
      await savedPostingsApi.save(postingId, session).catch(() => {
        setSavedIds(prev => { const s = new Set(prev); s.delete(postingId); return s })
      })
    }
  }

  const [selectedPosting, setSelectedPosting] = useState<Posting | null>(null)

  // Auto-open detail modal for deep-linked posting once items are loaded
  useEffect(() => {
    if (!loading && highlightPostingId && !selectedPosting) {
      const found = items.find(p => p.id === highlightPostingId)
      if (found) setSelectedPosting(found)
    }
  }, [loading, items])

  const remaining = total - items.length

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Panel */}
      <div className="relative bg-white w-full sm:max-w-2xl rounded-2xl flex flex-col max-h-[90dvh] sm:max-h-[80dvh] overflow-x-hidden">

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 shrink-0">
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-semibold text-gray-900 text-base leading-snug">{alert.name}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0" aria-label="Chiudi">
              <X size={18} />
            </button>
          </div>
          <FilterPills filters={alert.filters} />
        </div>

        {/* Backfill banner */}
        {backfill && (
          <div className="mx-6 mt-4 rounded-lg bg-gray-100 px-4 py-3 text-xs text-gray-500 leading-relaxed">
            Questi bandi sono già online. Riceverai una email per tutti i nuovi bandi pubblicati da adesso in avanti.
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <svg className="animate-spin h-7 w-7 text-sky-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              <span className="text-gray-400 text-sm">Caricamento bandi…</span>
            </div>
          ) : error ? (
            <p className="text-red-600 text-sm py-8 text-center">{error}</p>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center py-14 text-center">
              <BellOff size={40} className="text-gray-300 mb-4" />
              <p className="font-semibold text-gray-800 text-sm mb-1">Nessun bando trovato</p>
              <p className="text-gray-400 text-xs max-w-xs leading-relaxed">
                Controlliamo ogni giorno — ti avviseremo via email quando vengono pubblicati nuovi bandi.
              </p>
            </div>
          ) : (
            <>
              <ul className="pt-4">
                {items.map((p, i) => (
                  <PostingRow
                    key={p.id}
                    posting={p}
                    zebra={i % 2 === 1}
                    highlight={p.id === highlightPostingId}
                    saved={savedIds.has(p.id)}
                    onToggleSave={() => toggleSave(p.id)}
                    onOpenDetail={() => setSelectedPosting(p)}
                  />
                ))}
              </ul>

              {/* Load more / all loaded */}
              <div className="py-4 text-center">
                {hasMore ? (
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="text-sm font-medium text-sky-600 hover:text-sky-800 disabled:opacity-50 transition-colors"
                  >
                    {loadingMore ? (
                      <span className="inline-flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-sky-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                        Caricamento…
                      </span>
                    ) : `Carica altri ${remaining}`}
                  </button>
                ) : (
                  <p className="text-xs text-gray-400">Tutti i bandi caricati</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && (
          <div className="px-6 py-3 border-t border-gray-100 shrink-0">
            <p className="text-xs text-gray-400">
              {total === 0
                ? 'Nessun bando corrispondente'
                : `Trovati ${total} band${total === 1 ? 'o' : 'i'} corrispondent${total === 1 ? 'e' : 'i'}`
              }
            </p>
          </div>
        )}
      </div>

      {/* Posting detail modal — rendered inside the same z-50 context, at z-60 */}
      {selectedPosting && (
        <PostingDetailModal
          posting={selectedPosting}
          saved={savedIds.has(selectedPosting.id)}
          onToggleSave={() => toggleSave(selectedPosting.id)}
          onClose={() => setSelectedPosting(null)}
        />
      )}
    </div>
  )
}
